import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const features = [
  {
    title: 'AI-Powered Content Generation',
    description: 'Generate compelling content effortlessly, with AI-powered precision.',
    image: '/images/solutions/ai-growth-engine/event-creation.jpg',
    bullets: [
      'Instantly create social media posts, emails, reports, and more.',
      'Optimized for engagement across platforms like LinkedIn, Instagram, Facebook, and Twitter.',
      'Customizable prompts tailored for nonprofit storytelling.'
    ]
  },
  {
    title: 'Context-Driven AI Content',
    description: 'Leverages data from Knowledge Hub to generate relevant and impactful content.',
    image: '/images/solutions/ai-growth-engine/volunteer-management.jpg',
    bullets: [
      'Ensures consistency by referencing past projects, reports, and nonprofit goals.',
      'AI adapts based on historical insights to refine future content.',
      'Smart recommendations for optimizing messaging and engagement.'
    ]
  },
  {
    title: 'Tailored for Nonprofits',
    description: 'Designed specifically to meet the unique content needs of nonprofits.',
    image: '/images/solutions/ai-growth-engine/event-booking.jpg',
    bullets: [
      'Pre-built templates for impact reports, volunteer recognition, fundraising campaigns, and more.',
      'Automated volunteer recruitment emails, event promotion images, and thank-you messages.',
      'AI-powered event branding with auto-generated visuals and descriptions.'
    ]
  },
];

export const AIGrowthEnginePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-3xl font-bold mb-5">
              AI Growth Engine
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-6">
              The Ultimate AI Content Creation Tool for Nonprofits
            </p>
            <p className="text-lg text-gray-200">
              Generate high-quality, AI-driven content tailored for your nonprofits. From volunteer outreach to impact reporting content creation streamlines.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        {features.map((feature, index) => (
          <div 
            key={feature.title}
            className={`flex flex-col md:flex-row items-center gap-12 py-16 ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{feature.title}</h2>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-3">
                {feature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#2D7D46] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Content Creation?
          </h2>
          <p className="text-xl mb-8">
            Unlock the power of AI-driven storytelling and engagement today.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#2D7D46] hover:bg-gray-100"
            onClick={() => navigate("/signup")}
          >
            Start Using AI Growth Engine
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIGrowthEnginePage;